"use client";

import React from "react";
import { Phone, ShieldCheck, Clock, MapPin, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  return <section id="hero" data-nav="dark" className="relative min-h-[580px] md:min-h-[640px] lg:min-h-[700px] flex items-center justify-center overflow-hidden bg-foreground text-background">
      {/* Background Image with Responsive Tags & Fallback Scrim */}
      <div className="absolute inset-0 z-0 select-none overflow-hidden">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/biohazard_technician_containment.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/biohazard_technician_containment.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/biohazard_technician_containment.png-wvc-sizes" alt="Certified restoration technician in specialized protective equipment decontaminating a secured site" loading="eager" decoding="async" className="h-full w-full object-cover object-center scale-105 transform" />
        {/* Multi-stop industrial dark scrim for guaranteed contrast */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/80 to-black/60" />
      </div>

      {/* Decorative Technical Spec Bar Motif */}
      <div className="absolute top-0 inset-x-0 z-10 border-b border-border/20 bg-background/5 backdrop-blur-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-wrap items-center justify-between gap-3 text-xs tracking-wide text-background/80 font-mono">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <span>24/7 RAPID DISPATCH</span>
            <span className="text-border/40">•</span>
            <span>NO CALL CENTERS</span>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-background/70">
            <span className="flex items-center gap-1.5">
              <MapPin className="size-3 text-accent" />
              <span>Maryland • Washington DC • Northern Virginia</span>
            </span>
          </div>
        </div>
      </div>

      {/* Hero Content Container */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 lg:py-32 w-full flex flex-col items-start justify-center">
        <div className="max-w-3xl space-y-6">
          {/* Eyebrow badge */}
          <div className="inline-flex items-center gap-2 rounded-md border border-border/25 bg-background/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-background backdrop-blur-xs">
            <ShieldCheck className="size-3.5 text-accent" />
            <span>Certified Biohazard & Trauma Decontamination</span>
          </div>

          {/* Primary H1 */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight font-default">
            Biohazard Cleanup
          </h1>

          {/* Supporting Line */}
          <p className="text-lg sm:text-xl md:text-2xl text-background/90 font-medium leading-snug">
            Discreet, compassionate, and certified biohazard cleanup, trauma scene decontamination, and medical waste disposal across Maryland, DC, and Northern Virginia.
          </p>

          {/* Detailed Reassuring Body Copy */}
          <p className="text-sm sm:text-base text-background/75 leading-relaxed font-sans max-w-2xl">
            When traumatic events or biohazardous emergencies occur, our local field crews arrive directly in unmarked vehicles. We handle every phase of containment, safe disposal, and OSHA-compliant sanitation with complete privacy, coordinating directly with your insurance to lift the burden during difficult moments.
          </p>

          {/* Action CTAs */}
          <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 w-full sm:w-auto">
            {/* Direct Call Button */}
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold px-6 py-6 text-base shadow-lg transition-[background-color,transform] duration-200 hover:-translate-y-0.5 rounded-md flex items-center justify-center gap-2.5">
              <a href="tel:855-435-6631">
                <Phone className="size-5" />
                <span>Call Now</span>
              </a>
            </Button>

            {/* Request Service Button */}
            <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 border border-accent font-extrabold px-6 py-6 text-base shadow-lg transition-[background-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 rounded-md flex items-center justify-center gap-2">
              <Link to="/ContactRequestServicePage">
                <span>Request Service</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>

          {/* Operational Verification Row */}
          <div className="pt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-background/80 border-t border-border/20 max-w-2xl">
            <div className="flex items-center gap-2">
              <Clock className="size-4 text-emerald-400 shrink-0" />
              <span>Immediate 24/7 Dispatch</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="size-4 text-emerald-400 shrink-0" />
              <span>Direct Insurance Coordination</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="size-4 text-emerald-400 shrink-0" />
              <span>Local Crews, No Outsourcing</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}